import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Car, MapPin, Clock, CheckCircle, XCircle, Navigation, Navigation2,
  User, Phone, Settings, ArrowLeft, RefreshCw, Star, AlertCircle, LogOut,
  DollarSign, TrendingUp, ChevronRight, Loader2, Zap, Save, Shield, FileText,
  Bike, Package, ToggleLeft, ToggleRight, Timer, Coffee, Layers, List, MessageCircle, HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { driverAPI, messagesAPI } from '../services/api';
import FAQFeedback from '../components/FAQFeedback';
import { useMessagingSocket } from '../hooks/useMessagingSocket';

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = '&copy; OpenStreetMap contributors';

// Decode Google-encoded polyline (standard algorithm, no dependencies)
const decodePolyline = (encoded) => {
  if (!encoded) return [];
  const points = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let shift = 0, result = 0, byte;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
};

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, getAuthToken, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('map');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [availableRides, setAvailableRides] = useState([]);
  const [myRides, setMyRides] = useState([]);
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [earningsPeriod, setEarningsPeriod] = useState('week');
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [complianceData, setComplianceData] = useState(null);
  const [taxEstimate, setTaxEstimate] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);

  // Journey Log (UK 12-month retention compliance)
  const [journeyLogSummary, setJourneyLogSummary] = useState(null);
  const [journeyLog, setJourneyLog] = useState(null);
  const [journeyLogPage, setJourneyLogPage] = useState(1);
  const [journeyLogLoading, setJourneyLogLoading] = useState(false);
  const [showJourneyLog, setShowJourneyLog] = useState(false);

  // --- Unified Super App State ---
  const [servicePrefs, setServicePrefs] = useState(null);
  const [fatigueStatus, setFatigueStatus] = useState(null);
  const [stackedOffer, setStackedOffer] = useState(null);
  const [unifiedJobs, setUnifiedJobs] = useState([]);
  const [unifiedEarnings, setUnifiedEarnings] = useState(null);
  const [unifiedJobsFilter, setUnifiedJobsFilter] = useState('all');
  const [servicePrefsLoading, setServicePrefsLoading] = useState(false);
  const [addServiceLoading, setAddServiceLoading] = useState(false);

  // --- Messages Tab ---
  const [driverConversations, setDriverConversations] = useState([]);
  const [driverConvsLoading, setDriverConvsLoading] = useState(false);
  const [selectedDriverConv, setSelectedDriverConv] = useState(null);
  const [driverConvMessages, setDriverConvMessages] = useState([]);
  const [driverConvInput, setDriverConvInput] = useState('');
  const [driverConvSending, setDriverConvSending] = useState(false);
  const driverConvEndRef = useRef(null);

  // Map refs
  const mapContainerRef = useRef(null);  // holds the DOM node for non-init uses
  const mapInstanceRef = useRef(null);
  const rideMarkersRef = useRef([]);
  const routeLineRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const lastRouteFetchRef = useRef({ from: '', to: '' }); // dedup route fetches

  // Enhanced GPS state (watchPosition + Wake Lock)
  const gpsWatchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastSentRef = useRef(0);
  const [driverPosition, setDriverPosition] = useState(null);
  const [gpsPermissionDenied, setGpsPermissionDenied] = useState(false);

  // Geofence distances
  const [distanceToPickup, setDistanceToPickup] = useState(null);
  const [distanceToDropoff, setDistanceToDropoff] = useState(null);

  // Traffic detection (GPS speed + TomTom verification)
  const [trafficStatus, setTrafficStatus] = useState('normal');
  const [showTrafficOverlay, setShowTrafficOverlay] = useState(false);
  const speedHistoryRef = useRef([]);
  const stationarySinceRef = useRef(null);
  const lastTrafficCheckRef = useRef(0);  // timestamp of last TomTom check
  const trafficVerifiedRef = useRef(false);

  // Completion state (location-based verification replaces PIN)
  const [completingTrip, setCompletingTrip] = useState(false);

  // Active ride tracking
  const [activeRide, setActiveRide] = useState(null);
  const [selectedRide, setSelectedRide] = useState(null);
  const stackedOfferIntervalRef = useRef(null);

  // --- In-App Ride Chat ---
  const [showRideChat, setShowRideChat] = useState(false);
  const [rideChatMessages, setRideChatMessages] = useState([]);
  const [rideChatInput, setRideChatInput] = useState('');
  const [rideChatConvId, setRideChatConvId] = useState(null);
  const [rideChatLoading, setRideChatLoading] = useState(false);
  const [rideChatTyping, setRideChatTyping] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const chatEndRef = useRef(null);

  // --- Driver Cancellation Modal ---
  const [showDriverCancelModal, setShowDriverCancelModal] = useState(false);
  const [driverCancelReason, setDriverCancelReason] = useState('');
  const [driverCancelRideId, setDriverCancelRideId] = useState(null);
  const [driverCancelling, setDriverCancelling] = useState(false);

  // --- Data Fetching ---
  const fetchDashboard = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getDashboard(token);
      setDashboard(data);
      setProfile(data.driver);
      setProfileForm({
        fullName: data.driver?.fullName || '',
        phone: data.driver?.phone || '',
        city: data.driver?.city || '',
        vehicleType: data.driver?.vehicleType || '',
        plate: data.driver?.plate || '',
        licenseNumber: data.driver?.licenseNumber || ''
      });
      setAvailableRides(data.availableRides || []);
      setMyRides(data.myRides || []);

      // Check for active ride
      const active = (data.myRides || []).find(r =>
        ['accepted', 'en_route', 'arrived_pickup', 'in_progress'].includes(r.status)
      );
      if (active) setActiveRide(active);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 403) {
        toast({ title: 'Access Denied', description: 'You need driver access', variant: 'destructive' });
        navigate('/login', { replace: true });
      }
    }
  }, [getAuthToken, navigate, toast]);

  const fetchAvailableRides = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getAvailableRides(token);
      setAvailableRides(data);
      updateRideMarkers(data);
    } catch (error) {
      console.error('Failed to fetch available rides:', error);
    }
  }, [getAuthToken]);

  const fetchMyRides = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getMyRides(token);
      setMyRides(data);
    } catch (error) {
      console.error('Failed to fetch my rides:', error);
    }
  }, [getAuthToken]);

  const fetchEarnings = useCallback(async (period) => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getEarnings(period, token);
      setEarnings(data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    }
  }, [getAuthToken]);

  const fetchCompliance = useCallback(async () => {
    setComplianceLoading(true);
    try {
      const token = getAuthToken();
      const [compliance, tax, jlSummary] = await Promise.all([
        driverAPI.getCompliance(token),
        driverAPI.getTaxEstimate(token).catch(() => null),
        driverAPI.getJourneyLogSummary(token).catch(() => null)
      ]);
      setComplianceData(compliance);
      setTaxEstimate(tax);
      setJourneyLogSummary(jlSummary);
    } catch (error) {
      console.error('Failed to fetch compliance:', error);
    } finally {
      setComplianceLoading(false);
    }
  }, [getAuthToken]);

  const fetchJourneyLog = useCallback(async (page = 1) => {
    setJourneyLogLoading(true);
    try {
      const token = getAuthToken();
      const data = await driverAPI.getJourneyLog(token, 12, page, 20);
      setJourneyLog(data);
      setJourneyLogPage(page);
    } catch (error) {
      console.error('Failed to fetch journey log:', error);
    } finally {
      setJourneyLogLoading(false);
    }
  }, [getAuthToken]);

  // --- Driver Messages Fetchers ---
  const fetchDriverConversations = useCallback(async () => {
    setDriverConvsLoading(true);
    try {
      const token = getAuthToken();
      const convs = await messagesAPI.getConversations(token);
      // Filter to ride and lost_item conversations where driver is host
      const driverConvs = convs.filter(c =>
        ['ride', 'lost_item'].includes(c.conversationType) && c.hostId === user?._id
      );
      setDriverConversations(driverConvs);
    } catch (e) {
      console.error('Failed to fetch driver conversations:', e);
    }
    setDriverConvsLoading(false);
  }, [getAuthToken, user]);

  const loadDriverConvMessages = useCallback(async (conv) => {
    setSelectedDriverConv(conv);
    setDriverConvMessages([]);
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversation(conv._id, token);
      setDriverConvMessages(data.messages || []);
      // Update conv with fresh data (e.g. resolvedAt)
      if (data.conversation) {
        setSelectedDriverConv(prev => ({ ...prev, ...data.conversation }));
      }
      setTimeout(() => driverConvEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error('Failed to load messages:', e);
    }
  }, [getAuthToken]);

  const sendDriverConvMessage = useCallback(async () => {
    if (!driverConvInput.trim() || !selectedDriverConv) return;
    const filtered = filterPersonalInfo(driverConvInput.trim());
    setDriverConvSending(true);
    setDriverConvInput('');
    try {
      const token = getAuthToken();
      // Optimistic add
      const optimisticMsg = {
        _id: 'temp-' + Date.now(),
        conversationId: selectedDriverConv._id,
        senderId: user?._id,
        senderName: user?.name?.split(' ')[0] || 'You',
        content: filtered,
        createdAt: new Date().toISOString()
      };
      setDriverConvMessages(prev => [...prev, optimisticMsg]);
      await messagesAPI.sendMessage({ conversationId: selectedDriverConv._id, content: filtered }, token);
      setTimeout(() => driverConvEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send message';
      toast({ title: detail, variant: 'destructive' });
      // Remove optimistic message on 403 (ride ended / resolved)
      if (e.response?.status === 403) {
        setDriverConvMessages(prev => prev.filter(m => !m._id.startsWith('temp-')));
      }
    }
    setDriverConvSending(false);
  }, [driverConvInput, selectedDriverConv, getAuthToken, user, toast]);

  // --- Unified Super App Fetchers ---
  const fetchServicePrefs = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getServicePreferences(token);
      setServicePrefs(data);
    } catch (error) {
      console.error('Failed to fetch service preferences:', error);
    }
  }, [getAuthToken]);

  const fetchFatigueStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getFatigueStatus(token);
      setFatigueStatus(data);
    } catch (error) {
      console.error('Failed to fetch fatigue status:', error);
    }
  }, [getAuthToken]);

  const fetchStackedOffer = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getStackedOffer(token);
      setStackedOffer(data.offer || null);
    } catch (error) {
      console.error('Failed to fetch stacked offer:', error);
    }
  }, [getAuthToken]);

  const fetchUnifiedJobs = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getUnifiedJobs(50, token);
      setUnifiedJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch unified jobs:', error);
    }
  }, [getAuthToken]);

  const fetchUnifiedEarnings = useCallback(async (period) => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getUnifiedEarnings(period, token);
      setUnifiedEarnings(data);
    } catch (error) {
      console.error('Failed to fetch unified earnings:', error);
    }
  }, [getAuthToken]);

  // --- WebSocket for Ride Chat + Driver Messages Tab ---
  const handleNewMessage = useCallback((conversationId, message) => {
    // Ride chat during active ride
    if (conversationId === rideChatConvId && message.senderId !== user?._id) {
      setRideChatMessages(prev => [...prev, message]);
      if (!showRideChat) setUnreadChatCount(prev => prev + 1);
    }
    // Driver Messages tab — push new messages into the open conversation
    if (selectedDriverConv && conversationId === selectedDriverConv._id && message.senderId !== user?._id) {
      setDriverConvMessages(prev => {
        if (prev.find(m => m._id === message._id)) return prev;
        return [...prev, message];
      });
      setTimeout(() => driverConvEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
    // Update driver conversation list preview
    setDriverConversations(prev => prev.map(conv =>
      conv._id === conversationId
        ? { ...conv, lastMessage: message.content?.substring(0, 100), lastMessageAt: message.createdAt }
        : conv
    ));
  }, [rideChatConvId, showRideChat, user, selectedDriverConv]);

  const handleTyping = useCallback((conversationId, senderId, senderName, isTyping) => {
    if (conversationId === rideChatConvId && senderId !== user?._id) {
      setRideChatTyping(isTyping);
    }
  }, [rideChatConvId, user]);

  const { isConnected: wsConnected, sendTypingIndicator } = useMessagingSocket({
    onNewMessage: handleNewMessage,
    onTyping: handleTyping,
    activeConversationId: showRideChat ? rideChatConvId : selectedDriverConv?._id || null
  });

  // Load ride conversation when active ride changes
  useEffect(() => {
    if (!activeRide?.conversationId) {
      setRideChatConvId(null);
      setRideChatMessages([]);
      setShowRideChat(false);
      setUnreadChatCount(0);
      return;
    }
    const loadChat = async () => {
      try {
        const token = getAuthToken();
        const data = await messagesAPI.getConversation(activeRide.conversationId, token);
        setRideChatConvId(activeRide.conversationId);
        setRideChatMessages(data.messages || []);
      } catch (e) {
        console.error('Failed to load ride chat:', e);
      }
    };
    loadChat();
  }, [activeRide?.conversationId, getAuthToken]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [rideChatMessages]);

  // Filter personal info — blocks phone numbers, emails, social handles, addresses
  const filterPersonalInfo = (text) => {
    return text
      .replace(/(\+?\d[\d\s\-().]{7,}\d)/g, '[number hidden]')
      .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email hidden]')
      .replace(/@[\w]{2,}/g, '[handle hidden]')
      .replace(/\b\d{1,3}\s+[\w\s]+(?:road|street|lane|avenue|drive|close|way|place|court|crescent|terrace|grove|park)\b/gi, '[address hidden]');
  };

  const sendRideChatMessage = async (content) => {
    if (!content.trim() || !rideChatConvId) return;
    const filtered = filterPersonalInfo(content.trim());
    if (filtered !== content.trim()) {
      toast({ title: 'Personal info removed', description: 'Phone numbers, emails, and addresses are hidden for safety.' });
    }
    setRideChatLoading(true);
    setRideChatInput('');
    try {
      const token = getAuthToken();
      // Optimistically add message to local state so driver sees it immediately
      const optimisticMsg = {
        _id: 'temp-' + Date.now(),
        conversationId: rideChatConvId,
        senderId: user?._id,
        senderName: user?.name?.split(' ')[0] || 'You',
        content: filtered,
        createdAt: new Date().toISOString()
      };
      setRideChatMessages(prev => [...prev, optimisticMsg]);
      await messagesAPI.sendMessage({
        conversationId: rideChatConvId,
        content: filtered
      }, token);
    } catch (e) {
      toast({ title: 'Failed to send message', variant: 'destructive' });
    }
    setRideChatLoading(false);
  };

  // Smart reply options based on ride status
  const getSmartReplies = () => {
    if (!activeRide) return [];
    switch (activeRide.status) {
      case 'accepted':
      case 'en_route':
        return ['On my way 🚗', '5 minutes away', 'In traffic, arriving soon', 'Just left, be there shortly'];
      case 'arrived_pickup':
        return ["I'm here ✅", "I'm at the front entrance", `Look for ${profile?.vehiclePlate || 'my car'}`, 'Please come out'];
      case 'in_progress':
        return ['Almost there 📍', 'About 5 min left', 'Taking a faster route'];
      default:
        return [];
    }
  };

  // Check if driver is moving (for anti-distraction)
  const isDriverMoving = driverPosition?.speed != null && driverPosition.speed > 2.2; // >5 mph

  // Redirect if not authenticated — wait for auth to finish loading
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    const loadData = async () => {
      setLoading(true);
      await fetchDashboard();
      // Also fetch unified data in parallel
      fetchFatigueStatus();
      fetchServicePrefs();
      fetchStackedOffer();
      fetchDriverConversations();
      setLoading(false);
    };
    loadData();
  }, [authLoading, fetchDashboard, fetchFatigueStatus, fetchServicePrefs, fetchStackedOffer]);

  // Poll driver Messages tab conversation for new messages (fallback when WebSocket is down)
  useEffect(() => {
    if (!selectedDriverConv?._id) return;
    const pollInterval = setInterval(async () => {
      try {
        const token = getAuthToken();
        const data = await messagesAPI.getConversation(selectedDriverConv._id, token);
        setDriverConvMessages(data.messages || []);
        if (data.conversation) {
          setSelectedDriverConv(prev => ({ ...prev, ...data.conversation }));
        }
      } catch (e) { /* ignore */ }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [selectedDriverConv?._id, getAuthToken]);

  // Poll for stacked offers every 10 seconds when driver has an active job
  useEffect(() => {
    if (activeRide) {
      fetchStackedOffer();
      stackedOfferIntervalRef.current = setInterval(fetchStackedOffer, 10000);
    } else {
      setStackedOffer(null);
      if (stackedOfferIntervalRef.current) {
        clearInterval(stackedOfferIntervalRef.current);
        stackedOfferIntervalRef.current = null;
      }
    }
    return () => {
      if (stackedOfferIntervalRef.current) {
        clearInterval(stackedOfferIntervalRef.current);
        stackedOfferIntervalRef.current = null;
      }
    };
  }, [activeRide, fetchStackedOffer]);

  // --- Map Initialization via callback ref ---
  // Fires the instant the map div is mounted in the DOM (no timing issues).
  const mapRefCallback = useCallback((node) => {
    mapContainerRef.current = node;
    if (!node) {
      // Node unmounted — clean up map and all markers
      driverMarkerRef.current = null;
      pickupMarkerRef.current = null;
      dropoffMarkerRef.current = null;
      routeLineRef.current = null;
      lastRouteFetchRef.current = { from: '', to: '' };
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      return;
    }
    // Node mounted — init map (with retry if Leaflet CDN not ready yet)
    if (mapInstanceRef.current) return; // already initialised

    const initMap = () => {
      const L = window.L;
      if (!L || !mapContainerRef.current) return false;
      // Guard: if Leaflet already attached to this container
      if (mapContainerRef.current._leaflet_id) return true;

      const map = L.map(mapContainerRef.current, {
        center: [51.5074, -0.1278],
        zoom: 13,
        zoomControl: false
      });

      L.tileLayer(TILE_URL, { attribution: TILE_ATTR }).addTo(map);
      L.control.zoom({ position: 'bottomright' }).addTo(map);
      mapInstanceRef.current = map;

      // Force Leaflet to recalculate container size after layout paint (multiple passes)
      // Guard every deferred call — map may have been removed by a tab switch
      const safeInvalidate = () => {
        try { if (mapInstanceRef.current && mapContainerRef.current) mapInstanceRef.current.invalidateSize(); } catch (_) {}
      };
      requestAnimationFrame(() => {
        safeInvalidate();
        setTimeout(safeInvalidate, 100);
        setTimeout(safeInvalidate, 300);
        setTimeout(safeInvalidate, 600);
      });

      // Centre on driver's current location (async — must guard)
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          try {
            if (mapInstanceRef.current && mapContainerRef.current) {
              mapInstanceRef.current.setView([pos.coords.latitude, pos.coords.longitude], 14);
            }
          } catch (_) { /* map removed before geolocation returned */ }
        },
        () => {}
      );
      return true;
    };

    if (!initMap()) {
      // Leaflet CDN not loaded yet — retry until it is
      const retryId = setInterval(() => {
        if (initMap()) clearInterval(retryId);
      }, 150);
      setTimeout(() => clearInterval(retryId), 10000);
    }
  }, []);

  // When switching back to the Map tab, force Leaflet to re-measure (multiple passes)
  useEffect(() => {
    if (activeTab === 'map' && mapInstanceRef.current) {
      const safe = () => { try { mapInstanceRef.current?.invalidateSize(); } catch (_) {} };
      safe();
      setTimeout(safe, 50);
      setTimeout(safe, 200);
      setTimeout(safe, 500);
    }
  }, [activeTab]);

  // Update ride markers on map when available rides change
  useEffect(() => {
    if (activeTab === 'map') updateRideMarkers(availableRides);
  }, [availableRides, activeTab]);

  const updateRideMarkers = (rides) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current || !mapContainerRef.current) return;

    rideMarkersRef.current.forEach(m => { try { m.remove(); } catch (_) {} });
    rideMarkersRef.current = [];

    const bounds = [];

    rides.forEach(ride => {
      if (!ride.pickupLat || !ride.pickupLng) return;
      if (!mapInstanceRef.current) return; // guard mid-loop

      const icon = L.divIcon({
        className: 'ride-marker',
        html: `<div style="
          background:#1F3A68;color:white;padding:4px 8px;border-radius:8px;
          font-size:12px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);
          display:flex;align-items:center;gap:4px;
        ">
          <span style="font-size:14px">📍</span>
          £${(ride.estimatedFare || 0).toFixed(0)}
        </div>`,
        iconSize: [80, 30],
        iconAnchor: [40, 30]
      });

      try {
        const marker = L.marker([ride.pickupLat, ride.pickupLng], { icon })
          .addTo(mapInstanceRef.current);
        marker.on('click', () => setSelectedRide(ride));
        rideMarkersRef.current.push(marker);
        bounds.push([ride.pickupLat, ride.pickupLng]);
      } catch (_) { /* map container detached */ }
    });

    if (bounds.length > 0 && mapInstanceRef.current) {
      try { mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 }); } catch (_) {}
    }
  };

  // --- Enhanced GPS with watchPosition + Wake Lock ---
  useEffect(() => {
    const stats = dashboard?.stats;
    if (!stats?.isAvailable) {
      stopGpsWatch();
      return;
    }
    startGpsWatch();
    return () => stopGpsWatch();
  }, [dashboard?.stats?.isAvailable]);

  const startGpsWatch = async () => {
    if (gpsWatchIdRef.current) return;

    // Request Wake Lock to keep GPS active when screen dims
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      }
    } catch (e) { /* Wake Lock not supported or permission denied */ }

    // Re-acquire Wake Lock when tab becomes visible again
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current && dashboard?.stats?.isAvailable) {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
          }
        } catch (e) { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Start continuous GPS watching
    if (!navigator.geolocation) {
      setGpsPermissionDenied(true);
      return;
    }

    gpsWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsPermissionDenied(false);
        setDriverPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          speed: pos.coords.speed,
          heading: pos.coords.heading,
          timestamp: Date.now()
        });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGpsPermissionDenied(true);
        }
      },
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
  };

  const stopGpsWatch = () => {
    if (gpsWatchIdRef.current) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  };

  // Throttle server updates to every 5 seconds
  useEffect(() => {
    if (!driverPosition || !dashboard?.stats?.isAvailable) return;
    const now = Date.now();
    if (now - lastSentRef.current < 5000) return;
    lastSentRef.current = now;

    const sendLoc = async () => {
      try {
        const token = getAuthToken();
        await driverAPI.updateLocation(
          driverPosition.lat, driverPosition.lng,
          driverPosition.heading, driverPosition.speed,
          token
        );
      } catch (e) { /* silently fail */ }
    };
    sendLoc();
  }, [driverPosition]);

  // --- Haversine distance helper (meters) ---
  const haversineMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // --- Geofence: track distances to pickup and dropoff ---
  useEffect(() => {
    if (!driverPosition || !activeRide) {
      setDistanceToPickup(null);
      setDistanceToDropoff(null);
      return;
    }
    if (activeRide.pickupLat && activeRide.pickupLng) {
      setDistanceToPickup(Math.round(haversineMeters(
        driverPosition.lat, driverPosition.lng, activeRide.pickupLat, activeRide.pickupLng
      )));
    }
    if (activeRide.dropoffLat && activeRide.dropoffLng) {
      setDistanceToDropoff(Math.round(haversineMeters(
        driverPosition.lat, driverPosition.lng, activeRide.dropoffLat, activeRide.dropoffLng
      )));
    }
  }, [driverPosition, activeRide]);

  // --- Driver Marker: live-updates on map as GPS position changes ---
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstanceRef.current || !mapContainerRef.current) return;

    if (!driverPosition) {
      // No position — remove marker
      if (driverMarkerRef.current) {
        try { driverMarkerRef.current.remove(); } catch (_) {}
        driverMarkerRef.current = null;
      }
      return;
    }

    const rotation = driverPosition.heading != null ? driverPosition.heading : 0;
    const driverIcon = L.divIcon({
      className: 'driver-marker',
      html: `<div style="
        width:40px;height:40px;display:flex;align-items:center;justify-content:center;
        background:#1F3A68;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);
        transform:rotate(${rotation}deg);font-size:20px;
      ">🚗</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });

    try {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setLatLng([driverPosition.lat, driverPosition.lng]);
        driverMarkerRef.current.setIcon(driverIcon);
      } else {
        driverMarkerRef.current = L.marker([driverPosition.lat, driverPosition.lng], {
          icon: driverIcon, zIndexOffset: 1000
        }).addTo(mapInstanceRef.current);
        driverMarkerRef.current.bindPopup('You are here');
        // Centre map on driver when marker first appears
        try { mapInstanceRef.current.setView([driverPosition.lat, driverPosition.lng], 14); } catch (_) {}
      }
    } catch (_) { /* map container detached */ }
  }, [driverPosition?.lat, driverPosition?.lng, driverPosition?.heading]);

  // --- Pickup & Drop-off Markers for Active Ride ---
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstanceRef.current || !mapContainerRef.current) return;

    // Clear old markers
    if (pickupMarkerRef.current) { try { pickupMarkerRef.current.remove(); } catch (_) {} pickupMarkerRef.current = null; }
    if (dropoffMarkerRef.current) { try { dropoffMarkerRef.current.remove(); } catch (_) {} dropoffMarkerRef.current = null; }

    if (!activeRide) return;

    // Pickup marker (green)
    if (activeRide.pickupLat && activeRide.pickupLng) {
      const pickupIcon = L.divIcon({
        className: 'pickup-marker',
        html: `<div style="
          background:#16a34a;color:white;padding:4px 8px;border-radius:8px;
          font-size:12px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);
          display:flex;align-items:center;gap:4px;border:2px solid white;
        ">🟢 Pickup</div>`,
        iconSize: [90, 30],
        iconAnchor: [45, 30]
      });
      try {
        pickupMarkerRef.current = L.marker([activeRide.pickupLat, activeRide.pickupLng], { icon: pickupIcon })
          .addTo(mapInstanceRef.current);
        pickupMarkerRef.current.bindPopup(activeRide.pickupAddress || 'Pickup location');
      } catch (_) {}
    }

    // Drop-off marker (red)
    if (activeRide.dropoffLat && activeRide.dropoffLng) {
      const dropoffIcon = L.divIcon({
        className: 'dropoff-marker',
        html: `<div style="
          background:#dc2626;color:white;padding:4px 8px;border-radius:8px;
          font-size:12px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);
          display:flex;align-items:center;gap:4px;border:2px solid white;
        ">🔴 Drop-off</div>`,
        iconSize: [100, 30],
        iconAnchor: [50, 30]
      });
      try {
        dropoffMarkerRef.current = L.marker([activeRide.dropoffLat, activeRide.dropoffLng], { icon: dropoffIcon })
          .addTo(mapInstanceRef.current);
        dropoffMarkerRef.current.bindPopup(activeRide.dropoffAddress || 'Drop-off location');
      } catch (_) {}
    }
  }, [activeRide?._id, activeRide?.pickupLat, activeRide?.dropoffLat]);

  // --- Route Line: fetch from OSRM (free) and display driver → destination ---
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstanceRef.current || !mapContainerRef.current || !driverPosition) return;

    // Determine destination based on ride status
    let destLat, destLng;
    if (!activeRide) {
      // No active ride — clear route
      if (routeLineRef.current) { try { routeLineRef.current.remove(); } catch (_) {} routeLineRef.current = null; }
      lastRouteFetchRef.current = { from: '', to: '' };
      return;
    }

    if (['accepted', 'en_route'].includes(activeRide.status)) {
      // Driving to pickup
      destLat = activeRide.pickupLat;
      destLng = activeRide.pickupLng;
    } else if (['arrived_pickup', 'in_progress'].includes(activeRide.status)) {
      // Driving to drop-off
      destLat = activeRide.dropoffLat;
      destLng = activeRide.dropoffLng;
    }

    if (!destLat || !destLng) return;

    // Dedup: only re-fetch if driver moved > 50m or destination changed
    const fromKey = `${driverPosition.lat.toFixed(4)},${driverPosition.lng.toFixed(4)}`;
    const toKey = `${destLat.toFixed(4)},${destLng.toFixed(4)}`;
    if (lastRouteFetchRef.current.from === fromKey && lastRouteFetchRef.current.to === toKey) return;
    lastRouteFetchRef.current = { from: fromKey, to: toKey };

    // Fetch route from OSRM (free, no API key)
    const fetchRoute = async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${driverPosition.lng},${driverPosition.lat};${destLng},${destLat}?overview=full&geometries=polyline`;
        const res = await fetch(url);
        const data = await res.json();
        if (!data.routes || !data.routes[0]) return;

        const points = decodePolyline(data.routes[0].geometry);
        if (points.length < 2) return;

        // Clear old route
        if (routeLineRef.current) { try { routeLineRef.current.remove(); } catch (_) {} }

        if (!mapInstanceRef.current || !mapContainerRef.current) return;

        routeLineRef.current = L.polyline(points, {
          color: '#1F3A68', weight: 5, opacity: 0.7, dashArray: null
        }).addTo(mapInstanceRef.current);

        // Fit map bounds to show driver + route + destination
        const bounds = [
          [driverPosition.lat, driverPosition.lng],
          [destLat, destLng]
        ];
        try { mapInstanceRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 }); } catch (_) {}
      } catch (e) {
        // OSRM unavailable — draw simple straight line as fallback
        if (!mapInstanceRef.current || !mapContainerRef.current) return;
        if (routeLineRef.current) { try { routeLineRef.current.remove(); } catch (_) {} }
        try {
          routeLineRef.current = L.polyline(
            [[driverPosition.lat, driverPosition.lng], [destLat, destLng]],
            { color: '#1F3A68', weight: 3, opacity: 0.5, dashArray: '10, 10' }
          ).addTo(mapInstanceRef.current);
        } catch (_) {}
      }
    };
    fetchRoute();
  }, [driverPosition?.lat, driverPosition?.lng, activeRide?._id, activeRide?.status]);

  // --- Traffic Detection (GPS speed trigger + TomTom verification) ---
  useEffect(() => {
    if (!driverPosition || !activeRide || activeRide.status !== 'in_progress') {
      setTrafficStatus('normal');
      setShowTrafficOverlay(false);
      stationarySinceRef.current = null;
      speedHistoryRef.current = [];
      trafficVerifiedRef.current = false;
      return;
    }

    const speedMph = (driverPosition.speed || 0) * 2.237; // m/s to mph
    const now = Date.now();
    speedHistoryRef.current.push({ speed: speedMph, timestamp: now });
    if (speedHistoryRef.current.length > 10) speedHistoryRef.current.shift();

    // Check last 3 pings for slow speed (GPS trigger only)
    const recent = speedHistoryRef.current.slice(-3);
    const gpsDetectsSlow = recent.length >= 3 && recent.every(p => p.speed < 5);

    if (gpsDetectsSlow) {
      // GPS says slow — verify with TomTom (rate-limited to once per 2 min)
      if (now - lastTrafficCheckRef.current > 120_000) {
        lastTrafficCheckRef.current = now;
        const verifyTraffic = async () => {
          try {
            const token = getAuthToken();
            const result = await driverAPI.checkTraffic(
              driverPosition.lat, driverPosition.lng, token
            );
            if (result.source === 'tomtom') {
              trafficVerifiedRef.current = true;
              if (result.inTraffic) {
                setTrafficStatus('slow');
              } else {
                // TomTom says road is clear — driver is just stopped, not traffic
                setTrafficStatus('normal');
              }
            } else {
              // TomTom unavailable (no API key) — fall back to GPS-only
              trafficVerifiedRef.current = false;
              setTrafficStatus('slow');
            }
          } catch {
            // API error — fall back to GPS-only
            trafficVerifiedRef.current = false;
            setTrafficStatus('slow');
          }
        };
        verifyTraffic();
      }
      // Between checks, keep the last verified status (don't flicker)
      if (!stationarySinceRef.current) {
        stationarySinceRef.current = now;
      } else if (now - stationarySinceRef.current > 5 * 60 * 1000) {
        setShowTrafficOverlay(true);
      }
    } else {
      setTrafficStatus('normal');
      stationarySinceRef.current = null;
      trafficVerifiedRef.current = false;
    }
  }, [driverPosition]);

  // --- Ride Actions ---
  const handleAcceptRide = async (rideId) => {
    try {
      const token = getAuthToken();
      const result = await driverAPI.acceptRide(rideId, token);
      setActiveRide(result);
      setSelectedRide(null);
      toast({ title: 'Ride Accepted!', description: 'Navigate to the pickup location' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to accept ride', variant: 'destructive' });
    }
  };

  // --- Driver Cancellation Reasons (Uber-style) ---
  const DRIVER_CANCEL_REASONS = [
    { id: 'stuck_in_traffic', label: 'Stuck in traffic', icon: '🚦' },
    { id: 'too_far_away', label: 'Pickup is too far away', icon: '📍' },
    { id: 'passenger_no_show', label: 'Passenger not at pickup', icon: '👤' },
    { id: 'vehicle_issue', label: 'Vehicle breakdown / issue', icon: '🔧' },
    { id: 'unsafe_area', label: 'Safety concern at pickup', icon: '⚠️' },
    { id: 'rider_requested', label: 'Rider asked me to cancel', icon: '💬' },
    { id: 'wrong_address', label: 'Wrong pickup address', icon: '🗺️' },
    { id: 'personal_emergency', label: 'Personal emergency', icon: '🆘' },
    { id: 'other', label: 'Other reason', icon: '📝' },
  ];

  const handleUpdateRideStatus = async (rideId, status, completionPin = null, cancellationReason = null) => {
    // Intercept cancellation: show reason modal instead of direct API call
    if (status === 'cancelled' && !cancellationReason) {
      setDriverCancelRideId(rideId);
      setDriverCancelReason('');
      setShowDriverCancelModal(true);
      return;
    }

    // For completion — no PIN needed. Backend verifies driver+guest proximity.
    if (status === 'completed') {
      setCompletingTrip(true);
    }

    try {
      const token = getAuthToken();
      const result = await driverAPI.updateRideStatus(rideId, status, token, null, cancellationReason);

      if (['completed', 'cancelled'].includes(status)) {
        setActiveRide(null);
        setShowDriverCancelModal(false);
        toast({ title: status === 'cancelled' ? 'Ride Cancelled' : 'Ride Completed ✓' });
      } else {
        setActiveRide(result);
        const labels = {
          en_route: 'On the way to pickup',
          arrived_pickup: 'Arrived at pickup — wait for passenger',
          in_progress: 'Trip started — driving to destination'
        };
        toast({ title: labels[status] || `Status: ${status}` });
      }
      fetchDashboard();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update ride';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    }
    setCompletingTrip(false);
  };

  // Confirm driver cancellation with reason
  const handleDriverCancelConfirm = async () => {
    if (!driverCancelRideId || !driverCancelReason) return;
    setDriverCancelling(true);
    const reasonLabel = DRIVER_CANCEL_REASONS.find(r => r.id === driverCancelReason)?.label || driverCancelReason;
    await handleUpdateRideStatus(driverCancelRideId, 'cancelled', null, reasonLabel);
    setDriverCancelling(false);
  };

  const handleToggleAvailability = async () => {
    try {
      const token = getAuthToken();
      const result = await driverAPI.toggleAvailability(token);
      toast({
        title: result.isAvailable ? 'You are now ONLINE' : 'You are now OFFLINE',
        description: result.isAvailable ? 'You will receive ride requests' : 'You will not receive new requests'
      });
      fetchDashboard();
      fetchFatigueStatus();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update availability';
      // Fatigue-related rejection
      if (detail.toLowerCase().includes('break') || detail.toLowerCase().includes('fatigue') || detail.toLowerCase().includes('duty')) {
        toast({ title: 'Mandatory Break Required', description: detail, variant: 'destructive' });
        fetchFatigueStatus();
      } else {
        toast({ title: 'Error', description: detail, variant: 'destructive' });
      }
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const token = getAuthToken();
      await driverAPI.updateProfile(profileForm, token);
      toast({ title: 'Profile Updated' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update profile', variant: 'destructive' });
    }
    setProfileSaving(false);
  };

  // --- Stacked Offer Handlers ---
  const handleAcceptStackedOffer = async (jobId) => {
    try {
      const token = getAuthToken();
      await driverAPI.acceptStackedOffer(jobId, token);
      toast({ title: 'Stacked Job Accepted!', description: 'It will activate when your current job completes' });
      setStackedOffer(prev => prev ? { ...prev, status: 'accepted' } : null);
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to accept offer', variant: 'destructive' });
    }
  };

  const handleDeclineStackedOffer = async (jobId) => {
    try {
      const token = getAuthToken();
      await driverAPI.declineStackedOffer(jobId, token);
      toast({ title: 'Offer Declined' });
      setStackedOffer(null);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to decline offer', variant: 'destructive' });
    }
  };

  const handleCancelStackedOffer = async (jobId) => {
    try {
      const token = getAuthToken();
      const result = await driverAPI.cancelStackedOffer(jobId, token);
      toast({
        title: 'Stacked Job Cancelled',
        description: result.blocked
          ? `Stacking paused for 1 hour (${result.recentCancellations} cancellations in 24h)`
          : 'Job returned to the pool',
      });
      setStackedOffer(null);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel stacked job', variant: 'destructive' });
    }
  };

  // --- Service Preferences Handlers ---
  const handleToggleStackedJobs = async () => {
    setServicePrefsLoading(true);
    try {
      const token = getAuthToken();
      const newVal = !(servicePrefs?.acceptStackedJobs);
      const data = await driverAPI.updateServicePreferences({ acceptStackedJobs: newVal }, token);
      setServicePrefs(data);
      toast({ title: newVal ? 'Back-to-back jobs enabled' : 'Back-to-back jobs disabled' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update preferences', variant: 'destructive' });
    }
    setServicePrefsLoading(false);
  };

  const handleAddService = async (serviceType) => {
    setAddServiceLoading(true);
    try {
      const token = getAuthToken();
      const data = await driverAPI.addService(serviceType, token);
      setServicePrefs(data);
      toast({ title: `${serviceType === 'delivery' ? 'Delivery' : 'Taxi'} service added!`, description: 'You can now accept jobs for this service' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Cannot Add Service', description: error.response?.data?.detail || 'Missing required documents', variant: 'destructive' });
    }
    setAddServiceLoading(false);
  };

  // --- Status Helpers ---
  const getStatusInfo = (status) => {
    const map = {
      requested: { label: 'Requested', color: 'bg-amber-100 text-amber-800' },
      accepted: { label: 'Accepted', color: 'bg-blue-100 text-blue-800' },
      en_route: { label: 'En Route', color: 'bg-purple-100 text-purple-800' },
      arrived_pickup: { label: 'At Pickup', color: 'bg-cyan-100 text-cyan-800' },
      in_progress: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
      completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800' }
    };
    return map[status] || { label: status, color: 'bg-gray-100' };
  };

  // --- Next Action Button for Active Ride (with geofencing) ---
  const getNextAction = (ride) => {
    if (ride.status === 'accepted') {
      return { label: 'Start Driving to Pickup', status: 'en_route', icon: <Navigation size={16} />, color: 'bg-purple-600 hover:bg-purple-700' };
    }
    if (ride.status === 'en_route') {
      const within100m = distanceToPickup !== null && distanceToPickup <= 100;
      return {
        label: within100m ? 'I Have Arrived' : `Arriving... (${distanceToPickup ? `${distanceToPickup}m` : '...'})`,
        status: 'arrived_pickup',
        icon: <MapPin size={16} />,
        color: within100m ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-gray-400',
        disabled: !within100m
      };
    }
    if (ride.status === 'arrived_pickup') {
      return { label: 'Start Trip', status: 'in_progress', icon: <Navigation2 size={16} />, color: 'bg-indigo-600 hover:bg-indigo-700' };
    }
    if (ride.status === 'in_progress') {
      const within200m = distanceToDropoff !== null && distanceToDropoff <= 200;
      return {
        label: within200m ? 'Complete Trip' : `Near drop-off... (${distanceToDropoff ? `${distanceToDropoff}m` : '...'})`,
        status: 'completed',
        icon: <CheckCircle size={16} />,
        color: within200m ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400',
        disabled: !within200m
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-[#1F3A68]" data-testid="driver-name">
                  {profile?.fullName || 'Driver Portal'}
                </h1>
                <p className="text-xs text-gray-500">{profile?.city} · {profile?.vehicleType}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className={`rounded-full px-4 ${stats.isAvailable
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
                onClick={handleToggleAvailability}
                data-testid="toggle-availability-btn"
              >
                <div className={`w-2 h-2 rounded-full mr-2 ${stats.isAvailable ? 'bg-white animate-pulse' : 'bg-gray-500'}`} />
                {stats.isAvailable ? 'Online' : 'Offline'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/', { replace: true }); setTimeout(() => logout(), 0); }}>
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-[#1F3A68] text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1">
              <CheckCircle size={14} />
              <span>{stats.totalRides || 0} rides</span>
            </div>
            <div className="flex items-center gap-1">
              <Navigation size={14} />
              <span>{stats.activeRides || 0} active</span>
            </div>
            <div className="flex items-center gap-1">
              <Star size={14} className="text-amber-400" />
              <span>{(stats.rating || 0).toFixed(1)}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="text-white/80 hover:text-white" onClick={fetchDashboard}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Fatigue Warning Banner */}
      {fatigueStatus && fatigueStatus.mustBreak && (
        <div className="bg-red-600 text-white px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <Coffee size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">Maximum duty time reached — you've been taken offline</p>
              <p className="text-xs text-white/80">
                You've been on duty for {fatigueStatus.onDutyHours?.toFixed(1)}h. A mandatory 6-hour break is required.
                {fatigueStatus.breakEndTime && ` You can go back online at ${new Date(fatigueStatus.breakEndTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.`}
              </p>
            </div>
          </div>
        </div>
      )}
      {fatigueStatus && fatigueStatus.isWarning && !fatigueStatus.mustBreak && (
        <div className="bg-amber-500 text-white px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <Timer size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">You've been online for {fatigueStatus.onDutyHours?.toFixed(1)} hours</p>
              <p className="text-xs text-white/90">Consider taking a break soon. Maximum continuous duty is {fatigueStatus.maxHours || 10} hours.</p>
            </div>
          </div>
        </div>
      )}

      {/* Active Ride Banner */}
      {activeRide && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-4 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-white/70">Active Ride</p>
                <p className="font-bold text-lg">#{activeRide.rideNumber}</p>
              </div>
              <Badge className="bg-white/20 text-white">{getStatusInfo(activeRide.status).label}</Badge>
            </div>

            <div className="flex items-start gap-3 mb-3">
              <div className="space-y-2 flex-1">
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1 rounded-full bg-green-400 flex-shrink-0" />
                  <p className="text-sm">{activeRide.pickupAddress}</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1 rounded-full bg-red-400 flex-shrink-0" />
                  <p className="text-sm">{activeRide.dropoffAddress}</p>
                </div>
              </div>
              {activeRide.estimatedFare > 0 && (
                <div className="text-right">
                  <p className="text-2xl font-bold">£{activeRide.estimatedFare.toFixed(2)}</p>
                </div>
              )}
            </div>

            {/* Google Maps Navigate Button */}
            <div className="mb-3">
              {(() => {
                // Determine navigation destination based on ride status
                const isPickupPhase = ['accepted', 'en_route', 'arrived_pickup'].includes(activeRide.status);
                const destLat = isPickupPhase ? activeRide.pickupLat : activeRide.dropoffLat;
                const destLng = isPickupPhase ? activeRide.pickupLng : activeRide.dropoffLng;
                const destLabel = isPickupPhase ? 'Navigate to Pickup' : 'Navigate to Drop-off';
                if (!destLat || !destLng) return null;
                return (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}&travelmode=driving`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
                  >
                    🧭 {destLabel} (Google Maps)
                  </a>
                );
              })()}
            </div>

            {/* Guest Info + Chat & Call */}
            <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                {(activeRide.guestName || 'G')[0]}
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{activeRide.guestName?.split(' ')[0] || 'Guest'}</p>
                {distanceToPickup != null && ['accepted', 'en_route'].includes(activeRide.status) && (
                  <p className="text-xs text-white/60">{distanceToPickup < 1000 ? `${distanceToPickup}m away` : `${(distanceToPickup / 1000).toFixed(1)}km away`}</p>
                )}
              </div>
              {/* Chat Button */}
              {rideChatConvId && (
                <button
                  onClick={() => { setShowRideChat(!showRideChat); setUnreadChatCount(0); }}
                  className="relative p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Chat with passenger"
                >
                  💬
                  {unreadChatCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{unreadChatCount}</span>
                  )}
                </button>
              )}
              {/* Proximity-Locked Call Button */}
              {activeRide.guestPhone && (() => {
                const isPickupPhase = ['accepted', 'en_route'].includes(activeRide.status);
                const canCall = !isPickupPhase || (distanceToPickup != null && distanceToPickup <= 500);
                return canCall ? (
                  <a href={`tel:${activeRide.guestPhone}`} className="p-2 bg-green-500/80 rounded-full hover:bg-green-500 transition-colors" title="Call passenger">
                    <Phone size={16} />
                  </a>
                ) : (
                  <div className="p-2 bg-white/10 rounded-full opacity-50 cursor-not-allowed" title={`Call unlocks within 500m (${distanceToPickup ? Math.round(distanceToPickup) + 'm away' : 'calculating...'})`}>
                    <Phone size={16} className="text-white/50" />
                  </div>
                );
              })()}
            </div>

            {/* In-App Ride Chat Panel */}
            {showRideChat && rideChatConvId && (
              <div className="bg-white rounded-lg mb-3 overflow-hidden shadow-lg">
                {/* Anti-distraction overlay when driving */}
                {isDriverMoving && activeRide.status === 'in_progress' && (
                  <div className="bg-red-600 text-white px-3 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
                    ⚠️ Safety First: Pull over before typing. Use quick replies below.
                  </div>
                )}
                {/* Messages */}
                <div className="h-48 overflow-y-auto p-3 space-y-2 bg-gray-50">
                  {rideChatMessages.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No messages yet. Send a quick reply to coordinate pickup.</p>
                  )}
                  {rideChatMessages.map((msg, i) => {
                    const isMe = msg.senderId === user?._id;
                    return (
                      <div key={msg._id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-3 py-1.5 rounded-2xl text-sm ${isMe ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border rounded-bl-sm'}`}>
                          <p>{msg.content}</p>
                          <p className={`text-xs mt-0.5 ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                            {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {rideChatTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-200 text-gray-600 px-3 py-1.5 rounded-2xl text-sm italic">typing...</div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                {/* Smart Reply Buttons */}
                <div className="px-3 py-2 border-t bg-white flex gap-1.5 overflow-x-auto no-scrollbar">
                  {getSmartReplies().map((reply, i) => (
                    <button
                      key={i}
                      onClick={() => sendRideChatMessage(reply)}
                      className="flex-shrink-0 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
                {/* Text Input — disabled when driving during in_progress */}
                {!(isDriverMoving && activeRide.status === 'in_progress') && (
                  <div className="px-3 py-2 border-t flex gap-2">
                    <input
                      type="text"
                      value={rideChatInput}
                      onChange={(e) => {
                        setRideChatInput(e.target.value);
                        if (rideChatConvId) sendTypingIndicator(rideChatConvId, true);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && sendRideChatMessage(rideChatInput)}
                      placeholder="Type a message..."
                      className="flex-1 text-sm border rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
                    />
                    <button
                      onClick={() => sendRideChatMessage(rideChatInput)}
                      disabled={!rideChatInput.trim() || rideChatLoading}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium disabled:opacity-50 hover:bg-blue-700 transition-colors"
                    >
                      Send
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Next Action */}
            {(() => {
              const next = getNextAction(activeRide);
              if (!next) return null;
              return (
                <div className="flex gap-2">
                  {/* Traffic indicator — only shows when TomTom confirms or as fallback */}
                  {trafficStatus === 'slow' && activeRide.status === 'in_progress' && (
                    <div className="flex items-center gap-1 bg-amber-500/20 text-amber-200 rounded-full px-3 py-1 text-xs mb-2 w-full">
                      <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                      Slow traffic detected {trafficVerifiedRef.current ? '(verified)' : ''}
                    </div>
                  )}
                  <Button
                    className={`flex-1 ${next.color} text-white h-12 text-base`}
                    onClick={() => handleUpdateRideStatus(activeRide._id, next.status)}
                    disabled={next.disabled}
                  >
                    {next.icon}
                    <span className="ml-2">{next.label}</span>
                  </Button>
                  {activeRide.status !== 'in_progress' && (
                    <Button
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10"
                      onClick={() => handleUpdateRideStatus(activeRide._id, 'cancelled')}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Stacked Job Offer Banner */}
      {stackedOffer && (
        <div className={`${stackedOffer.status === 'accepted' ? 'bg-gradient-to-r from-blue-600 to-indigo-700' : 'bg-gradient-to-r from-emerald-600 to-teal-700'} text-white px-4 py-4`}>
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <Layers size={18} />
              <p className="font-bold text-sm">
                {stackedOffer.status === 'accepted'
                  ? `✅ Queued ${stackedOffer.jobType === 'taxi' ? '🚕 Ride' : '🛵 Delivery'} — Activates After Current Job`
                  : `New ${stackedOffer.jobType === 'taxi' ? '🚕 Ride' : '🛵 Delivery'} Near Your Drop-off`}
              </p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 mb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1 flex-1">
                  {stackedOffer.pickupAddress && (
                    <div className="flex items-start gap-2">
                      <div className="w-2.5 h-2.5 mt-1.5 rounded-full bg-green-400 flex-shrink-0" />
                      <p className="text-sm">{stackedOffer.pickupAddress}</p>
                    </div>
                  )}
                  {stackedOffer.dropoffAddress && (
                    <div className="flex items-start gap-2">
                      <div className="w-2.5 h-2.5 mt-1.5 rounded-full bg-red-400 flex-shrink-0" />
                      <p className="text-sm">{stackedOffer.dropoffAddress}</p>
                    </div>
                  )}
                  {stackedOffer.distanceMiles != null && (
                    <p className="text-xs text-white/70 mt-1">
                      ~{stackedOffer.distanceMiles?.toFixed(1)} mi from your current drop-off
                    </p>
                  )}
                </div>
                {stackedOffer.estimatedEarnings > 0 && (
                  <div className="text-right ml-3">
                    <p className="text-xl font-bold">£{stackedOffer.estimatedEarnings?.toFixed(2)}</p>
                    <p className="text-xs text-white/70">Est. earnings</p>
                  </div>
                )}
              </div>
            </div>
            {stackedOffer.status === 'accepted' ? (
              <Button
                variant="outline"
                className="w-full border-white/30 text-white hover:bg-red-500/80 hover:border-red-400 h-10"
                onClick={() => handleCancelStackedOffer(stackedOffer.jobId)}
              >
                <XCircle size={16} className="mr-1" /> Cancel Stacked Job
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-white text-emerald-700 hover:bg-white/90 font-bold h-10"
                  onClick={() => handleAcceptStackedOffer(stackedOffer.jobId)}
                >
                  <CheckCircle size={16} className="mr-1" /> Accept
                </Button>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 h-10"
                  onClick={() => handleDeclineStackedOffer(stackedOffer.jobId)}
                >
                  <XCircle size={16} className="mr-1" /> Decline
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b sticky top-[52px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar py-2">
            {[
              { id: 'map', label: 'Map', icon: <MapPin size={14} /> },
              { id: 'rides', label: 'Rides', icon: <Car size={14} />, count: availableRides.length },
              { id: 'myrides', label: 'My Rides', icon: <Navigation size={14} /> },
              { id: 'alljobs', label: 'All Jobs', icon: <List size={14} /> },
              { id: 'earnings', label: 'Earnings', icon: <DollarSign size={14} /> },
              { id: 'compliance', label: 'Compliance', icon: <Shield size={14} /> },
              { id: 'messages', label: 'Messages', icon: <MessageCircle size={14} />, count: driverConversations.filter(c => c.unreadHost > 0).length },
              { id: 'profile', label: 'Profile', icon: <User size={14} /> },
              { id: 'faq', label: 'FAQ', icon: <HelpCircle size={14} /> },
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'rides') fetchAvailableRides();
                  if (tab.id === 'myrides') fetchMyRides();
                  if (tab.id === 'alljobs') fetchUnifiedJobs();
                  if (tab.id === 'earnings') { fetchEarnings(earningsPeriod); fetchUnifiedEarnings(earningsPeriod); }
                  if (tab.id === 'compliance') fetchCompliance();
                  if (tab.id === 'messages') fetchDriverConversations();
                  if (tab.id === 'profile') fetchServicePrefs();
                }}
                className={`flex-shrink-0 ${activeTab === tab.id ? 'bg-[#1F3A68]' : ''}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                <span className="ml-1">{tab.label}</span>
                {tab.count > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">{tab.count}</Badge>
                )}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* === MAP TAB === */}
        {activeTab === 'map' && (
          <div data-testid="map-tab">
            {!stats.isAvailable && (
              <Card className="bg-amber-50 border-amber-200 mb-4">
                <CardContent className="p-3 flex items-center gap-3">
                  <AlertCircle size={20} className="text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">You're offline. Go online to see and accept ride requests.</p>
                </CardContent>
              </Card>
            )}

            <div
              ref={mapRefCallback}
              className="w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-lg border border-gray-200"
              data-testid="driver-map"
            />

            {/* Selected Ride Card (overlay on map) */}
            {selectedRide && (
              <Card className="mt-4 border-2 border-[#1F3A68] shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg">#{selectedRide.rideNumber}</h3>
                        {selectedRide.estimatedFare > 0 && (
                          <Badge className="bg-green-100 text-green-800 text-base px-2">
                            £{selectedRide.estimatedFare.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <User size={14} /> {selectedRide.guestName?.split(' ')[0] || 'Guest'}
                        {selectedRide.passengers > 1 && <span>· {selectedRide.passengers} riders</span>}
                      </p>
                      {selectedRide.etaMinutes != null && (
                        <p className="text-xs text-blue-600 mt-0.5 flex items-center gap-1">
                          <Clock size={12} /> ~{selectedRide.etaMinutes} min to pickup ({selectedRide.distanceToPickup} mi)
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setSelectedRide(null)}>
                      <XCircle size={20} />
                    </Button>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2">
                      <div className="w-3 h-3 mt-1.5 rounded-full bg-green-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Pickup</p>
                        <p className="font-medium text-sm">{selectedRide.pickupAddress}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-3 h-3 mt-1.5 rounded-full bg-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Drop-off</p>
                        <p className="font-medium text-sm">{selectedRide.dropoffAddress}</p>
                      </div>
                    </div>
                    {selectedRide.notes && (
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">Note: {selectedRide.notes}</p>
                    )}
                  </div>

                  {selectedRide.fareEstimate && (
                    <div className="bg-blue-50 p-2 rounded-lg text-xs space-y-1 mb-3">
                      <div className="flex justify-between"><span>Distance</span><span>{selectedRide.fareEstimate.distance} mi</span></div>
                      <div className="flex justify-between"><span>Est. Duration</span><span>~{selectedRide.fareEstimate.duration} min</span></div>
                      <div className="flex justify-between"><span>Vehicle</span><span className="capitalize">{selectedRide.vehicleType}</span></div>
                      <div className="flex justify-between font-bold border-t pt-1"><span>Your Earnings</span><span>£{(selectedRide.estimatedFare * 0.9).toFixed(2)}</span></div>
                    </div>
                  )}

                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 h-12 text-base"
                    onClick={() => handleAcceptRide(selectedRide._id)}
                    disabled={!stats.isAvailable}
                    data-testid={`accept-ride-${selectedRide._id}`}
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Accept Ride · £{(selectedRide.estimatedFare * 0.9).toFixed(2)} earnings
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Quick list under map */}
            {!selectedRide && availableRides.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-600">{availableRides.length} ride{availableRides.length !== 1 ? 's' : ''} nearby</p>
                {availableRides.slice(0, 3).map(ride => (
                  <div
                    key={ride._id}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-[#1F3A68] transition-colors"
                    onClick={() => setSelectedRide(ride)}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1F3A68] text-white flex items-center justify-center font-bold text-sm">
                      £{(ride.estimatedFare || 0).toFixed(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{ride.pickupAddress}</p>
                      <p className="text-xs text-gray-500 truncate">→ {ride.dropoffAddress}</p>
                      {ride.etaMinutes != null && <p className="text-xs text-blue-500">~{ride.etaMinutes} min away</p>}
                    </div>
                    <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === AVAILABLE RIDES TAB (list view) === */}
        {activeTab === 'rides' && (
          <div className="space-y-4" data-testid="available-rides-tab">
            {!stats.isAvailable && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3 flex items-center gap-3">
                  <AlertCircle size={20} className="text-amber-600" />
                  <p className="text-sm text-amber-800">Go online to accept ride requests</p>
                </CardContent>
              </Card>
            )}

            {availableRides.length === 0 ? (
              <Card className="p-8 text-center text-gray-500">
                <Car size={48} className="mx-auto mb-4 opacity-50" />
                <p className="font-medium">No ride requests nearby</p>
                <p className="text-sm">New requests will appear here</p>
              </Card>
            ) : (
              <div className="grid gap-3">
                {availableRides.map(ride => (
                  <Card key={ride._id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`available-ride-${ride._id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">#{ride.rideNumber}</h3>
                            <Badge className="bg-amber-100 text-amber-800 text-xs">{ride.vehicleType}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <User size={14} /> {ride.guestName?.split(' ')[0] || 'Guest'}
                            {ride.passengers > 1 && <span>· {ride.passengers} riders</span>}
                          </p>
                        </div>
                        {ride.estimatedFare > 0 && (
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-700">£{ride.estimatedFare.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">You earn £{(ride.estimatedFare * 0.9).toFixed(2)}</p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-start gap-2">
                          <div className="w-3 h-3 mt-1.5 rounded-full bg-green-500" />
                          <p className="text-sm">{ride.pickupAddress}</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-3 h-3 mt-1.5 rounded-full bg-red-500" />
                          <p className="text-sm">{ride.dropoffAddress}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <Clock size={12} />
                        <span>{ride.pickupTime === 'ASAP' ? 'ASAP' : ride.pickupTime}</span>
                        {ride.fareEstimate && (
                          <>
                            <span>·</span>
                            <span>{ride.fareEstimate.distance} mi</span>
                            <span>·</span>
                            <span>~{ride.fareEstimate.duration} min</span>
                          </>
                        )}
                      </div>

                      <Button
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => handleAcceptRide(ride._id)}
                        disabled={!stats.isAvailable}
                        data-testid={`accept-ride-${ride._id}`}
                      >
                        <CheckCircle size={14} className="mr-1" /> Accept Ride
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === MY RIDES TAB === */}
        {activeTab === 'myrides' && (
          <div className="space-y-3" data-testid="my-rides-tab">
            {myRides.length === 0 ? (
              <Card className="p-8 text-center text-gray-500">
                <Car size={48} className="mx-auto mb-4 opacity-50" />
                <p>No rides yet</p>
              </Card>
            ) : (
              myRides.map(ride => {
                const info = getStatusInfo(ride.status);
                const next = getNextAction(ride);
                return (
                  <Card key={ride._id} data-testid={`my-ride-${ride._id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">#{ride.rideNumber}</h3>
                            <Badge className={info.color}>{info.label}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            <User size={14} className="inline mr-1" />
                            {ride.guestName?.split(' ')[0] || 'Guest'}
                            {ride.guestPhone && (
                              <a href={`tel:${ride.guestPhone}`} className="ml-2 text-blue-600">
                                <Phone size={12} className="inline mr-1" />{ride.guestPhone}
                              </a>
                            )}
                          </p>
                        </div>
                        {ride.estimatedFare > 0 && (
                          <p className="font-bold text-lg">£{ride.estimatedFare.toFixed(2)}</p>
                        )}
                      </div>

                      <div className="space-y-1 mb-3">
                        <div className="flex items-start gap-2">
                          <div className="w-2.5 h-2.5 mt-1.5 rounded-full bg-green-500" />
                          <p className="text-sm">{ride.pickupAddress}</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2.5 h-2.5 mt-1.5 rounded-full bg-red-500" />
                          <p className="text-sm">{ride.dropoffAddress}</p>
                        </div>
                      </div>

                      {next && (
                        <div className="flex gap-2">
                          <Button
                            className={`flex-1 ${next.color} text-white`}
                            onClick={() => handleUpdateRideStatus(ride._id, next.status)}
                          >
                            {next.icon} <span className="ml-1">{next.label}</span>
                          </Button>
                          {ride.status !== 'in_progress' && (
                            <Button
                              variant="outline"
                              className="text-red-600"
                              onClick={() => handleUpdateRideStatus(ride._id, 'cancelled')}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* === UNIFIED ALL JOBS TAB === */}
        {activeTab === 'alljobs' && (
          <div className="space-y-4" data-testid="alljobs-tab">
            {/* Filter Buttons */}
            <div className="flex gap-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'taxi', label: '🚕 Taxi' },
                { id: 'delivery', label: '🛵 Delivery' },
              ].map(f => (
                <Button
                  key={f.id}
                  variant={unifiedJobsFilter === f.id ? 'default' : 'outline'}
                  size="sm"
                  className={unifiedJobsFilter === f.id ? 'bg-[#1F3A68]' : ''}
                  onClick={() => setUnifiedJobsFilter(f.id)}
                >
                  {f.label}
                </Button>
              ))}
            </div>

            {/* Jobs List */}
            {unifiedJobs.length === 0 ? (
              <Card className="p-8 text-center">
                <List size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">No jobs found</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {unifiedJobs
                  .filter(job => unifiedJobsFilter === 'all' || job.type === unifiedJobsFilter)
                  .map((job, idx) => {
                    const isTaxi = job.type === 'taxi';
                    const statusInfo = isTaxi ? getStatusInfo(job.status) : {
                      label: job.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || job.status,
                      color: job.status === 'delivered' ? 'bg-green-100 text-green-800' :
                             job.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                             'bg-blue-100 text-blue-800'
                    };
                    const dateStr = job.date ? new Date(job.date).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : '';
                    return (
                      <Card key={job.jobId || idx} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{isTaxi ? '🚕' : '🛵'}</span>
                              <span className="font-medium text-sm">
                                {isTaxi ? `Ride #${job.rideNumber || '—'}` : `Delivery #${job.orderId || '—'}`}
                              </span>
                            </div>
                            <Badge className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</Badge>
                          </div>
                          {job.pickupAddress && (
                            <div className="flex items-start gap-2 text-xs text-gray-600 mb-1">
                              <div className="w-2 h-2 mt-1 rounded-full bg-green-500 flex-shrink-0" />
                              <span>{job.pickupAddress}</span>
                            </div>
                          )}
                          {job.dropoffAddress && (
                            <div className="flex items-start gap-2 text-xs text-gray-600 mb-2">
                              <div className="w-2 h-2 mt-1 rounded-full bg-red-500 flex-shrink-0" />
                              <span>{job.dropoffAddress}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{dateStr}</span>
                            {job.earnings > 0 && (
                              <span className="font-bold text-green-700">£{job.earnings.toFixed(2)}</span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* === EARNINGS TAB === */}
        {activeTab === 'earnings' && (
          <div className="space-y-4" data-testid="earnings-tab">
            {/* Period Selector */}
            <div className="flex gap-2">
              {['today', 'week', 'month', 'all'].map(p => (
                <Button
                  key={p}
                  variant={earningsPeriod === p ? 'default' : 'outline'}
                  size="sm"
                  className={earningsPeriod === p ? 'bg-[#1F3A68]' : ''}
                  onClick={() => { setEarningsPeriod(p); fetchEarnings(p); }}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
            </div>

            {/* Summary Cards */}
            {earnings && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="p-4">
                      <DollarSign size={20} className="mb-1 opacity-80" />
                      <p className="text-2xl font-bold">£{earnings.netEarnings.toFixed(2)}</p>
                      <p className="text-white/70 text-xs">Net Earnings</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <CardContent className="p-4">
                      <Car size={20} className="mb-1 opacity-80" />
                      <p className="text-2xl font-bold">{earnings.totalRides}</p>
                      <p className="text-white/70 text-xs">Completed Rides</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-600">Total Fares</p>
                      <p className="text-xl font-bold">£{earnings.totalFares.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-600">Commission ({earnings.commissionRate})</p>
                      <p className="text-xl font-bold text-red-600">-£{earnings.totalCommission.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Insurance Deduction */}
                {earnings.insuranceDeducted > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-amber-800">Zego Insurance (£0.35/hr)</p>
                        <p className="text-xs text-amber-600">Covers your hire & reward insurance while on trips</p>
                      </div>
                      <p className="text-lg font-bold text-amber-700">-£{earnings.insuranceDeducted.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Daily Breakdown */}
                {earnings.daily && earnings.daily.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Daily Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {earnings.daily.map(day => (
                          <div key={day.date} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <p className="font-medium text-sm">{new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                              <p className="text-xs text-gray-500">{day.rides} ride{day.rides !== 1 ? 's' : ''}</p>
                            </div>
                            <p className="font-bold text-green-700">£{day.earnings.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Unified Earnings Breakdown (if driver does both services) */}
            {unifiedEarnings && (servicePrefs?.serviceTypes?.length > 1) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Layers size={16} /> Combined Service Earnings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🚕</span>
                        <div>
                          <p className="font-medium text-sm">Taxi Earnings</p>
                          <p className="text-xs text-gray-500">{unifiedEarnings.taxi?.jobCount || 0} rides</p>
                        </div>
                      </div>
                      <p className="font-bold text-green-700">£{(unifiedEarnings.taxi?.netEarnings || 0).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🛵</span>
                        <div>
                          <p className="font-medium text-sm">Delivery Earnings</p>
                          <p className="text-xs text-gray-500">{unifiedEarnings.delivery?.jobCount || 0} deliveries</p>
                        </div>
                      </div>
                      <p className="font-bold text-green-700">£{(unifiedEarnings.delivery?.netEarnings || 0).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center justify-between py-2 bg-[#1F3A68]/5 rounded-lg px-3">
                      <p className="font-bold text-sm">Total Combined</p>
                      <p className="font-bold text-lg text-[#1F3A68]">£{(unifiedEarnings.totalNetEarnings || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {!earnings && (
              <Card className="p-8 text-center">
                <Loader2 size={24} className="mx-auto animate-spin text-gray-400 mb-2" />
                <p className="text-gray-500">Loading earnings...</p>
              </Card>
            )}
          </div>
        )}

        {/* === COMPLIANCE & TAX TAB === */}
        {activeTab === 'compliance' && (
          <div className="space-y-4" data-testid="compliance-tab">
            {complianceLoading ? (
              <Card className="p-8 text-center">
                <Loader2 size={24} className="mx-auto animate-spin text-gray-400 mb-2" />
                <p className="text-gray-500">Loading compliance data...</p>
              </Card>
            ) : complianceData ? (
              <>
                {/* Overall Status Banner */}
                <div className={`rounded-lg p-4 flex items-center gap-3 ${
                  complianceData.overallStatus === 'compliant' ? 'bg-green-50 border border-green-200' :
                  complianceData.overallStatus === 'warning' ? 'bg-amber-50 border border-amber-200' :
                  'bg-red-50 border border-red-200'
                }`}>
                  <Shield size={24} className={
                    complianceData.overallStatus === 'compliant' ? 'text-green-600' :
                    complianceData.overallStatus === 'warning' ? 'text-amber-600' : 'text-red-600'
                  } />
                  <div>
                    <p className="font-bold text-sm">
                      {complianceData.overallStatus === 'compliant' ? 'All Clear — Fully Compliant' :
                       complianceData.overallStatus === 'warning' ? 'Action Needed — Expiring Soon' :
                       'Blocked — Cannot Accept Jobs'}
                    </p>
                    {complianceData.blockers?.length > 0 && (
                      <ul className="text-xs text-red-700 mt-1 list-disc list-inside">
                        {complianceData.blockers.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    )}
                    {complianceData.warnings?.length > 0 && (
                      <ul className="text-xs text-amber-700 mt-1 list-disc list-inside">
                        {complianceData.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Insurance Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield size={16} /> Hire & Reward Insurance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {complianceData.insurance && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Status</span>
                          <Badge className={
                            complianceData.insurance.status === 'valid' ? 'bg-green-100 text-green-700' :
                            complianceData.insurance.status === 'expiring_soon' ? 'bg-amber-100 text-amber-700' :
                            complianceData.insurance.status === 'expired' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {complianceData.insurance.status?.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                        {complianceData.insurance.expiryDate && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Expires</span>
                            <span className="font-medium">{complianceData.insurance.expiryDate}</span>
                          </div>
                        )}
                        {complianceData.insurance.daysRemaining != null && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Days remaining</span>
                            <span className={`font-bold ${complianceData.insurance.daysRemaining < 30 ? 'text-amber-600' : 'text-green-600'}`}>
                              {complianceData.insurance.daysRemaining}
                            </span>
                          </div>
                        )}
                        {complianceData.insurance.provider && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Provider</span>
                            <span className="font-medium">{complianceData.insurance.provider}</span>
                          </div>
                        )}
                        {complianceData.insurance.action && (
                          <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                            {complianceData.insurance.action}
                          </p>
                        )}
                        {complianceData.insurance.providers && (
                          <p className="text-xs text-gray-500 mt-1">
                            Recommended providers: {complianceData.insurance.providers.join(', ')}
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Licenses Card */}
                {complianceData.licenses?.licenses?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText size={16} /> Licenses
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {complianceData.licenses.licenses.map((lic, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div>
                            <p className="text-sm font-medium">{lic.type === 'driving_license' ? 'Driving License' : 'PHV License'}</p>
                            <p className="text-xs text-gray-500">Expires: {lic.expiryDate}</p>
                          </div>
                          <Badge className={
                            lic.status === 'valid' ? 'bg-green-100 text-green-700' :
                            lic.status === 'expiring_soon' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {lic.daysRemaining} days
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Tax Estimate Card */}
                {taxEstimate && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText size={16} /> HMRC Tax Estimate ({taxEstimate.taxYear})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Gross Earnings</p>
                          <p className="text-lg font-bold">£{(taxEstimate.grossEarnings || 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Take Home</p>
                          <p className="text-lg font-bold text-green-700">£{(taxEstimate.takeHome || 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Est. Income Tax</p>
                          <p className="text-lg font-bold text-red-600">£{(taxEstimate.incomeTax || 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Est. NI (Class 2+4)</p>
                          <p className="text-lg font-bold text-red-600">£{((taxEstimate.class2NI || 0) + (taxEstimate.class4NI || 0)).toFixed(2)}</p>
                        </div>
                      </div>
                      {taxEstimate.mileageAllowance > 0 && (
                        <div className="mt-3 bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600">HMRC Mileage Allowance</p>
                          <p className="text-base font-bold text-blue-700">£{taxEstimate.mileageAllowance.toFixed(2)}</p>
                          <p className="text-xs text-blue-500">{taxEstimate.totalDistanceMiles?.toFixed(0)} miles @ 45p/mile</p>
                        </div>
                      )}
                      <p className="text-[10px] text-gray-400 mt-3 text-center">
                        {taxEstimate.disclaimer || 'This is an estimate. Consult an accountant for tax filing.'}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Journey Log Card — UK 12-Month Retention */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText size={16} /> UK Journey Log
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-auto">12-Month Retention</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800 font-medium">
                        Under UK PHV licensing, journey records must be retained for a minimum of 12 months.
                        All your completed journeys are stored securely and available for licensing authority audits.
                      </p>
                    </div>

                    {journeyLogSummary && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Total Journeys</p>
                          <p className="text-lg font-bold">{journeyLogSummary.totalJourneys}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Total Fares (12mo)</p>
                          <p className="text-lg font-bold text-green-700">£{(journeyLogSummary.totalFaresInWindow || 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Avg Fare</p>
                          <p className="text-lg font-bold">£{(journeyLogSummary.avgFareInWindow || 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Records Since</p>
                          <p className="text-sm font-bold">{journeyLogSummary.earliestJourney || '—'}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <Badge className={
                        journeyLogSummary?.complianceStatus === 'compliant'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }>
                        {journeyLogSummary?.complianceStatus === 'compliant' ? 'Compliant' : 'No journeys yet'}
                      </Badge>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowJourneyLog(!showJourneyLog);
                          if (!showJourneyLog && !journeyLog) fetchJourneyLog(1);
                        }}
                        className="text-xs"
                      >
                        <FileText size={14} className="mr-1" />
                        {showJourneyLog ? 'Hide Log' : 'View Full Log'}
                      </Button>
                    </div>

                    {/* Expanded Journey Log Table */}
                    {showJourneyLog && (
                      <div className="mt-3 border-t pt-3">
                        {journeyLogLoading ? (
                          <div className="text-center py-4">
                            <Loader2 size={20} className="mx-auto animate-spin text-gray-400" />
                            <p className="text-xs text-gray-400 mt-1">Loading journey records...</p>
                          </div>
                        ) : journeyLog?.journeyLog?.length > 0 ? (
                          <>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                              {journeyLog.journeyLog.map((j, idx) => (
                                <div key={j.journeyId || idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-bold text-gray-700">#{j.rideNumber}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-500">{j.date} · {j.time}</span>
                                      <Badge className="bg-green-100 text-green-700 text-[10px]">£{(j.fareCharged || 0).toFixed(2)}</Badge>
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-start gap-1.5">
                                      <div className="w-2 h-2 mt-1 rounded-full bg-green-500 flex-shrink-0" />
                                      <p className="text-xs text-gray-600 leading-tight">{j.pickupAddress}</p>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <div className="w-2 h-2 mt-1 rounded-full bg-red-500 flex-shrink-0" />
                                      <p className="text-xs text-gray-600 leading-tight">{j.dropoffAddress}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400">
                                    <span>{j.vehicleType}</span>
                                    {j.durationMinutes && <span>{j.durationMinutes} min</span>}
                                    {j.distance && <span>{typeof j.distance === 'number' ? j.distance.toFixed(1) : j.distance} mi</span>}
                                    {j.passengers > 1 && <span>{j.passengers} passengers</span>}
                                    {j.rated && <span>⭐ {j.rating}/5</span>}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Pagination */}
                            {journeyLog.totalPages > 1 && (
                              <div className="flex items-center justify-between mt-3 pt-2 border-t">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={journeyLogPage <= 1}
                                  onClick={() => fetchJourneyLog(journeyLogPage - 1)}
                                  className="text-xs"
                                >
                                  Previous
                                </Button>
                                <span className="text-xs text-gray-500">
                                  Page {journeyLog.page} of {journeyLog.totalPages} · {journeyLog.total} records
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={journeyLogPage >= journeyLog.totalPages}
                                  onClick={() => fetchJourneyLog(journeyLogPage + 1)}
                                  className="text-xs"
                                >
                                  Next
                                </Button>
                              </div>
                            )}

                            {/* Retention policy footer */}
                            <p className="text-[10px] text-gray-400 mt-2 text-center">
                              Records retained from {journeyLog.retentionPolicy?.cutoffDate} · {journeyLog.retentionPolicy?.legalBasis?.split('—')[0]}
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-gray-400 text-center py-4">No journey records found in the retention window.</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="p-8 text-center">
                <Shield size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">Click to load compliance data</p>
                <Button size="sm" className="mt-3 bg-[#1F3A68]" onClick={fetchCompliance}>
                  Check Compliance
                </Button>
              </Card>
            )}
          </div>
        )}

        {/* === MESSAGES TAB === */}
        {activeTab === 'messages' && (
          <div data-testid="messages-tab" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageCircle size={20} /> Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                {driverConvsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-gray-400" size={24} />
                  </div>
                ) : selectedDriverConv ? (
                  /* === Conversation View === */
                  <div>
                    {/* Back + header */}
                    <div className="flex items-center gap-3 mb-3 pb-3 border-b">
                      <button onClick={() => setSelectedDriverConv(null)} className="p-1 hover:bg-gray-100 rounded-full">
                        <ArrowLeft size={18} />
                      </button>
                      <div className="flex-1">
                        <p className="font-bold text-sm">{selectedDriverConv.guestName || 'Guest'}</p>
                        <p className="text-xs text-gray-500">
                          {selectedDriverConv.conversationType === 'lost_item' ? '🔍 Lost Item' : '🚕 Ride Chat'}
                          {selectedDriverConv.expiresAt && (
                            <span className="ml-2 text-orange-500">
                              · Expires {new Date(selectedDriverConv.expiresAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto mb-3 px-1">
                      {driverConvMessages.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">No messages yet</p>
                      ) : driverConvMessages.map(msg => {
                        const isMe = msg.senderId === user?._id;
                        return (
                          <div key={msg._id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                              isMe ? 'bg-[#1F3A68] text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'
                            }`}>
                              {!isMe && <p className="text-xs font-semibold mb-0.5">{msg.senderName}</p>}
                              <p>{msg.content}</p>
                              <p className={`text-[10px] mt-1 ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
                                {new Date(msg.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={driverConvEndRef} />
                    </div>

                    {/* Input — hidden for completed ride chats and resolved lost items */}
                    {selectedDriverConv.resolvedAt ? (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-center">
                        <p className="text-sm text-green-700 font-medium">✅ This lost item report has been resolved.</p>
                        <p className="text-xs text-green-600 mt-1">Messages are view-only.</p>
                      </div>
                    ) : selectedDriverConv.conversationType === 'ride' ? (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-center">
                        <p className="text-sm text-gray-600 font-medium">🔒 This ride has ended.</p>
                        <p className="text-xs text-gray-500 mt-1">Messaging is no longer available.</p>
                      </div>
                    ) : (
                    <div className="flex gap-2">
                      <Input
                        value={driverConvInput}
                        onChange={e => setDriverConvInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') sendDriverConvMessage(); }}
                        placeholder="Type a message..."
                        className="flex-1"
                      />
                      <Button
                        onClick={sendDriverConvMessage}
                        disabled={!driverConvInput.trim() || driverConvSending}
                        className="bg-[#1F3A68]"
                      >
                        Send
                      </Button>
                    </div>
                    )}
                  </div>
                ) : driverConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle size={40} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">No messages yet</p>
                    <p className="text-gray-400 text-sm mt-1">Ride and lost item conversations will appear here</p>
                  </div>
                ) : (
                  /* === Conversation List === */
                  <div className="space-y-2">
                    {driverConversations.map(conv => (
                      <button
                        key={conv._id}
                        onClick={() => loadDriverConvMessages(conv)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 text-left transition-all"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                          conv.conversationType === 'lost_item' ? 'bg-orange-100' : 'bg-blue-100'
                        }`}>
                          {conv.conversationType === 'lost_item' ? '🔍' : '💬'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm truncate">{conv.guestName || 'Guest'}</p>
                            {conv.lastMessageAt && (
                              <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {new Date(conv.lastMessageAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{conv.lastMessage || 'No messages'}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {conv.conversationType === 'lost_item' ? 'Lost Item' : 'Ride'}
                            </Badge>
                            {conv.unreadHost > 0 && (
                              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">{conv.unreadHost} new</Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* === PROFILE TAB === */}
        {activeTab === 'profile' && profile && (
          <div data-testid="profile-tab" className="space-y-4">
            {/* Driver Profile Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User size={20} /> Driver Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={profileForm.fullName}
                      onChange={e => setProfileForm(p => ({ ...p, fullName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={profileForm.phone}
                      onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>City / Service Area</Label>
                    <Input
                      value={profileForm.city}
                      onChange={e => setProfileForm(p => ({ ...p, city: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Vehicle Type</Label>
                    <select
                      value={profileForm.vehicleType}
                      onChange={e => setProfileForm(p => ({ ...p, vehicleType: e.target.value }))}
                      className="w-full h-10 px-3 border border-gray-200 rounded-md"
                    >
                      <option value="economy">Economy</option>
                      <option value="comfort">Comfort</option>
                      <option value="xl">XL</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <Label>License Plate</Label>
                    <Input
                      value={profileForm.plate}
                      onChange={e => setProfileForm(p => ({ ...p, plate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>License Number</Label>
                    <Input
                      value={profileForm.licenseNumber}
                      onChange={e => setProfileForm(p => ({ ...p, licenseNumber: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 pt-2">
                  <div className="flex-1 text-sm text-gray-500">
                    <span className="font-medium">Email:</span> {profile.email}
                    <span className="mx-2">·</span>
                    <span className="font-medium">Total Rides:</span> {profile.totalRides || 0}
                    <span className="mx-2">·</span>
                    <span className="font-medium">Rating:</span> {(profile.rating || 0).toFixed(1)}
                  </div>
                  <Button onClick={handleSaveProfile} disabled={profileSaving} className="bg-[#1F3A68]">
                    {profileSaving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Service Mode Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers size={20} /> Service Mode
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Active Services */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Active Services</Label>
                  <div className="flex gap-3">
                    <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 ${
                      servicePrefs?.serviceTypes?.includes('taxi')
                        ? 'border-[#1F3A68] bg-[#1F3A68]/5'
                        : 'border-gray-200 bg-gray-50'
                    }`}>
                      <span className="text-xl">🚕</span>
                      <div>
                        <p className="font-medium text-sm">Taxi</p>
                        <p className="text-xs text-gray-500">
                          {servicePrefs?.serviceTypes?.includes('taxi') ? 'Active' : 'Not active'}
                        </p>
                      </div>
                      {servicePrefs?.serviceTypes?.includes('taxi') && (
                        <CheckCircle size={16} className="text-green-600 ml-2" />
                      )}
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 ${
                      servicePrefs?.serviceTypes?.includes('delivery')
                        ? 'border-[#1F3A68] bg-[#1F3A68]/5'
                        : 'border-gray-200 bg-gray-50'
                    }`}>
                      <span className="text-xl">🛵</span>
                      <div>
                        <p className="font-medium text-sm">Delivery</p>
                        <p className="text-xs text-gray-500">
                          {servicePrefs?.serviceTypes?.includes('delivery') ? 'Active' : 'Not active'}
                        </p>
                      </div>
                      {servicePrefs?.serviceTypes?.includes('delivery') && (
                        <CheckCircle size={16} className="text-green-600 ml-2" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Add Service Button */}
                {servicePrefs && (
                  <>
                    {!servicePrefs.serviceTypes?.includes('delivery') && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800 mb-2">
                          Add delivery service to accept food delivery jobs alongside taxi rides.
                          You'll need right-to-work and National Insurance documents on file.
                        </p>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleAddService('delivery')}
                          disabled={addServiceLoading}
                        >
                          {addServiceLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Package size={14} className="mr-1" />}
                          Add Delivery Service
                        </Button>
                      </div>
                    )}
                    {!servicePrefs.serviceTypes?.includes('taxi') && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800 mb-2">
                          Add taxi service to accept ride requests. You'll need a PHV license, DBS certificate,
                          vehicle insurance (hire & reward), driving licence, and medical certificate on file.
                        </p>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleAddService('taxi')}
                          disabled={addServiceLoading}
                        >
                          {addServiceLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Car size={14} className="mr-1" />}
                          Add Taxi Service
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Back-to-Back Jobs Toggle */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm">Accept back-to-back jobs</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, you'll receive new job offers near your current drop-off
                        location before your current job ends — keeping you busy with minimal downtime.
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-3"
                      onClick={handleToggleStackedJobs}
                      disabled={servicePrefsLoading}
                    >
                      {servicePrefsLoading ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : servicePrefs?.acceptStackedJobs ? (
                        <ToggleRight size={28} className="text-green-600" />
                      ) : (
                        <ToggleLeft size={28} className="text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Fatigue Status */}
                {fatigueStatus && (
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Timer size={16} className="text-gray-600" />
                      <p className="font-medium text-sm">Duty Time Tracking</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Today's on-duty time</span>
                        <span className={`font-bold text-sm ${
                          fatigueStatus.onDutyHours >= 10 ? 'text-red-600' :
                          fatigueStatus.onDutyHours >= 8 ? 'text-amber-600' : 'text-green-600'
                        }`}>
                          {fatigueStatus.onDutyHours?.toFixed(1)}h / {fatigueStatus.maxHours || 10}h
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            fatigueStatus.onDutyHours >= 10 ? 'bg-red-500' :
                            fatigueStatus.onDutyHours >= 8 ? 'bg-amber-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min((fatigueStatus.onDutyHours / (fatigueStatus.maxHours || 10)) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* === FAQ TAB === */}
        {activeTab === 'faq' && (
          <FAQFeedback />
        )}
      </main>

      {/* GPS Permission Denied Overlay */}
      {gpsPermissionDenied && (
        <div className="fixed inset-0 z-50 bg-[#1F3A68] flex items-center justify-center p-6">
          <div className="text-center text-white max-w-sm">
            <AlertCircle size={64} className="mx-auto mb-4 text-amber-400" />
            <h2 className="text-2xl font-bold mb-3">Please Enable Location</h2>
            <p className="text-white/80 mb-6">
              We need your GPS location to show you on the map and enable geofenced actions like "Arrived at Pickup" and "Complete Trip".
            </p>
            <div className="bg-white/10 rounded-xl p-4 text-left text-sm text-white/70 mb-6 space-y-2">
              <p><strong>Chrome:</strong> Settings → Privacy → Site Settings → Location → Allow</p>
              <p><strong>Safari:</strong> Settings → Privacy → Location Services → Browser → Allow</p>
              <p><strong>Firefox:</strong> Click the lock icon in the address bar → Permissions → Location → Allow</p>
            </div>
            <Button
              onClick={() => { setGpsPermissionDenied(false); startGpsWatch(); }}
              className="bg-white text-[#1F3A68] hover:bg-gray-100 w-full h-12 text-base font-bold"
            >
              Retry Location Access
            </Button>
          </div>
        </div>
      )}

      {/* Trip Completing Overlay */}
      {completingTrip && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-bold text-[#1F3A68] mb-2">Completing Trip...</h3>
            <p className="text-sm text-gray-500">Verifying drop-off location</p>
          </div>
        </div>
      )}

      {/* Traffic Overlay — "Are you in traffic?" */}
      {showTrafficOverlay && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <span className="text-5xl mb-3 block">🚦</span>
            <h3 className="text-xl font-bold text-[#1F3A68] mb-2">Are you in traffic?</h3>
            <p className="text-gray-500 text-sm mb-6">
              You've been stationary for 5+ minutes. This helps us explain delays to your passenger.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowTrafficOverlay(false); stationarySinceRef.current = null; }}
              >
                No, I'm fine
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={async () => {
                  setShowTrafficOverlay(false);
                  setTrafficStatus('slow');
                  // Send inTraffic=true flag to backend
                  try {
                    const token = getAuthToken();
                    if (driverPosition) {
                      await driverAPI.updateLocation(
                        driverPosition.lat, driverPosition.lng,
                        driverPosition.heading, driverPosition.speed,
                        token,
                        true  // inTraffic = true (driver confirmed)
                      );
                    }
                  } catch (e) { /* ignore */ }
                }}
              >
                Yes, in traffic
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === Driver Cancellation Reason Modal (Uber-style) === */}
      {showDriverCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => { setShowDriverCancelModal(false); setDriverCancelReason(''); }}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-lg font-bold text-gray-900">Cancel this ride?</h3>
              <button onClick={() => { setShowDriverCancelModal(false); setDriverCancelReason(''); }} className="p-1 hover:bg-gray-100 rounded-full">
                <XCircle size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Warning */}
            <div className="px-4 pt-3">
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">Frequent cancellations may affect your driver rating and account status. Only cancel when necessary.</p>
              </div>
            </div>

            {/* Reason list */}
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">Why are you cancelling this ride?</p>
              <div className="space-y-2">
                {DRIVER_CANCEL_REASONS.map(reason => (
                  <button
                    key={reason.id}
                    onClick={() => setDriverCancelReason(reason.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                      driverCancelReason === reason.id
                        ? 'border-[#1F3A68] bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl flex-shrink-0">{reason.icon}</span>
                    <span className={`text-sm font-medium ${driverCancelReason === reason.id ? 'text-[#1F3A68]' : 'text-gray-700'}`}>
                      {reason.label}
                    </span>
                    {driverCancelReason === reason.id && (
                      <span className="ml-auto text-[#1F3A68]">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t p-4 space-y-2">
              <Button
                onClick={handleDriverCancelConfirm}
                disabled={!driverCancelReason || driverCancelling}
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-base disabled:opacity-50"
              >
                {driverCancelling ? <Loader2 className="animate-spin mr-2" size={18} /> : <XCircle size={18} className="mr-2" />}
                {driverCancelling ? 'Cancelling...' : 'Yes, Cancel Ride'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowDriverCancelModal(false); setDriverCancelReason(''); }}
                className="w-full h-12 text-base border-gray-300"
              >
                Keep This Ride
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDashboard;
